// components/Contact.jsx
import React, { useEffect, useRef } from 'react';
import TransitionLink from './TransitionLink';
import Camera3D from './Camera3D';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../translations/translations';

const Contact = () => {
    const { language } = useLanguage();
    const t = useTranslation(language);
    const sectionRef = useRef(null);
    const itemsRef = useRef([]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('animate-in');
                    }
                });
            },
            { threshold: 0.1 }
        );

        // Observe the section
        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        // Observe each text element
        itemsRef.current.forEach((item) => {
            if (item) observer.observe(item);
        });

        return () => {
            observer.disconnect();
        };
    }, []);

    return (
        <section
            ref={sectionRef}
            id="contact"
            className="w-full bg-black py-16 md:py-24 pt-24 md:pt-32 transition-opacity duration-700 opacity-0 relative overflow-visible"
        >
            <div className="container mx-auto px-8 md:px-16 relative overflow-visible">
                {/* 3D Camera positioned on the right side */}
                <div className="absolute right-64 top-1/2 -translate-y-1/2 w-[500px] h-[500px] hidden lg:block pointer-events-auto camera-3d-model">
                    <Camera3D />
                </div>

                <div className="max-w-5xl">
                    {/* Main paragraph */}
                    <p
                        ref={(el) => (itemsRef.current[0] = el)}
                        className="text-2xl md:text-3xl text-white leading-tight opacity-0 transform translate-y-10 transition-all duration-700 delay-100 text-left font-light tracking-tight"
                        style={{ fontFamily: "'Space Grotesk', 'Archivo', sans-serif" }}
                    >
                        {t('contactIntro')}
                    </p>

                    {/* Email address and Social Icons */}
                    <div
                        ref={(el) => (itemsRef.current[1] = el)}
                        className="flex flex-col md:flex-row md:items-center space-y-6 md:space-y-0 md:space-x-8 mt-12 opacity-0 transform translate-y-10 transition-all duration-700 delay-200"
                    >
                        <a
                            href="mailto:info@paoloallegretti.com"
                            className="text-3xl md:text-5xl text-white font-medium tracking-tight hover:text-accent contact-email-link"
                            style={{ fontFamily: "'Space Grotesk', 'Archivo', sans-serif" }}
                        >
                            info@paoloallegretti.com
                        </a>

                        {/* Social Icons */}
                        {/* <div className="flex space-x-4 md:space-x-6 md:translate-x-24">
                            {/* ArtStation */}
                        {/* <a href="https://artstation.com/paolowho" target="_blank" rel="noopener noreferrer" className="text-white hover:text-accent transition-colors">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
                                    <path d="M0 17.723l2.027 3.505h.001a2.424 2.424 0 0 0 2.164 1.333h13.457l-2.792-4.838H0zm24 .025c0-.484-.143-.935-.388-1.314L15.728 2.728a2.424 2.424 0 0 0-2.142-1.289H9.419L21.598 22.54l1.92-3.325c.378-.637.482-.919.482-1.467zm-11.129-3.462L7.428 4.858l-5.444 9.428h10.887z"></path>
                                </svg>
                            </a> */}

                        {/* LinkedIn */}
                        {/* <a href="https://linkedin.com/in/paoloallegretti" target="_blank" rel="noopener noreferrer" className="text-white hover:text-accent transition-colors">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
                                    <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z" />
                                </svg>
                            </a> */}

                        {/* X (Twitter) */}
                        {/* <a href="https://x.com/pa0x__" target="_blank" rel="noopener noreferrer" className="text-white hover:text-accent transition-colors">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                </svg>
                            </a> */}
                        {/* </div> */}
                    </div>

                    {/* Navigation Links */}
                    <div
                        ref={(el) => (itemsRef.current[2] = el)}
                        className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-8 mt-12 opacity-0 transform translate-y-10 transition-all duration-700 delay-300"
                    >
                        <TransitionLink
                            to="/about"
                            className="header-nav-link relative inline-flex items-center text-white font-medium group overflow-hidden"
                            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                        >
                            <span className="relative z-10 inline-flex items-center transition-transform duration-500 group-hover:-translate-y-full">
                                {t('aboutMe')}
                                <span className="ml-2 transition-transform duration-300 group-hover:translate-x-1">→</span>
                            </span>
                            <span className="absolute left-0 top-full z-0 inline-flex items-center text-accent transition-transform duration-500 group-hover:-translate-y-full">
                                {t('aboutMe')}
                                <span className="ml-2 translate-x-3">→</span>
                            </span>
                            <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-accent transition-all duration-500 ease-out group-hover:w-full"></span>
                        </TransitionLink>
                        <TransitionLink
                            to="/works"
                            className="header-nav-link relative inline-flex items-center text-white font-medium group overflow-hidden"
                            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                        >
                            <span className="relative z-10 inline-flex items-center transition-transform duration-500 group-hover:-translate-y-full">
                                {t('goToWorks')}
                                <span className="ml-2 transition-transform duration-300 group-hover:translate-x-1">→</span>
                            </span>
                            <span className="absolute left-0 top-full z-0 inline-flex items-center text-accent transition-transform duration-500 group-hover:-translate-y-full">
                                {t('goToWorks')}
                                <span className="ml-2 translate-x-3">→</span>
                            </span>
                            <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-accent transition-all duration-500 ease-out group-hover:w-full"></span>
                        </TransitionLink>

                        <TransitionLink
                            to="/code"
                            className="header-nav-link relative inline-flex items-center text-white font-medium group overflow-hidden"
                            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                        >
                            <span className="relative z-10 inline-flex items-center transition-transform duration-500 group-hover:-translate-y-full">
                                {t('goToDeveloper')}
                                <span className="ml-2 transition-transform duration-300 group-hover:translate-x-1">→</span>
                            </span>
                            <span className="absolute left-0 top-full z-0 inline-flex items-center text-accent transition-transform duration-500 group-hover:-translate-y-full">
                                {t('goToDeveloper')}
                                <span className="ml-2 translate-x-3">→</span>
                            </span>
                            <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-accente transition-all duration-500 ease-out group-hover:w-full"></span>
                        </TransitionLink>


                        {/* Playground link - Desktop only */}
                        <TransitionLink
                            to="/playground"
                            className="header-nav-link relative hidden md:inline-flex items-center text-white font-medium group overflow-hidden"
                            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                        >
                            <span className="relative z-10 inline-flex items-center transition-transform duration-500 group-hover:-translate-y-full">
                                {t('playgroundLink')}
                                <span className="ml-2 transition-transform duration-300 group-hover:translate-x-1">→</span>
                            </span>
                            <span className="absolute left-0 top-full z-0 inline-flex items-center text-accent transition-transform duration-500 group-hover:-translate-y-full">
                                {t('playgroundLink')}
                                <span className="ml-2 translate-x-3">→</span>
                            </span>
                            <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-accent transition-all duration-500 ease-out group-hover:w-full"></span>
                        </TransitionLink>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Contact;